import { isProductionRuntime } from '@/lib/app-env';
import { ROUTES } from '@/lib/route-paths';
import { useSession } from '@/lib/session';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeft,
  ChartLine,
  DatabaseZap,
  FileText,
  Film,
  FlaskConical,
  Layers,
  Megaphone,
  MegaphoneOff,
  MessageSquare,
  Music,
  PlugZap,
  Shield,
  Users,
  Video,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type * as React from 'react';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';

const IMPERSONATE_PREFIX = '/admin';
const VIDEO_PREFIX = '/admin/template-builder';

interface AdminNavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  isActive: (pathname: string) => boolean;
  items?: {
    title: string;
    url: string;
    icon: LucideIcon;
    isActive: (pathname: string) => boolean;
  }[];
}

const isImpersonateActive = (pathname: string) =>
  pathname === IMPERSONATE_PREFIX ||
  pathname === `${IMPERSONATE_PREFIX}/` ||
  pathname.startsWith('/admin/organizations');

const isVideoActive = (pathname: string) => pathname.startsWith(VIDEO_PREFIX);

const AUDIT_LOG_PREFIX = '/admin/audit-log';

const isAuditLogActive = (pathname: string) =>
  pathname.startsWith(AUDIT_LOG_PREFIX);

const META_CONNECTIONS_PREFIX = '/admin/meta-connections';

const isMetaConnectionsActive = (pathname: string) =>
  pathname.startsWith(META_CONNECTIONS_PREFIX);

const CONVERSATIONS_PREFIX = '/admin/conversations';
const ADVERTISING_PREFIX = '/admin/advertising';
const IMPORTS_PREFIX = '/admin/imports';

const isConversationsActive = (pathname: string) =>
  pathname.startsWith(CONVERSATIONS_PREFIX);
const isAdvertisingActive = (pathname: string) =>
  pathname.startsWith(ADVERTISING_PREFIX);
const isImportsActive = (pathname: string) =>
  pathname.startsWith(IMPORTS_PREFIX);

const STOCK_FOOTAGE_PREFIX = '/admin/stock-footage';
const isStockFootageActive = (pathname: string) =>
  pathname.startsWith(STOCK_FOOTAGE_PREFIX);

const CLAIRE_REGRESSIONS_PREFIX = '/admin/claire-regressions';
const isClaireRegressionsActive = (pathname: string) =>
  pathname.startsWith(CLAIRE_REGRESSIONS_PREFIX);

const CLAIRE_FAILURE_GROUPS_PREFIX = '/admin/claire-failure-groups';
const isClaireFailureGroupsActive = (pathname: string) =>
  pathname.startsWith(CLAIRE_FAILURE_GROUPS_PREFIX);

const STUCK_SETUP_PREFIX = '/admin/stuck-setup';
const isStuckSetupActive = (pathname: string) =>
  pathname.startsWith(STUCK_SETUP_PREFIX);

const HARNESS_PREFIX = '/admin/harness';
const isHarnessActive = (pathname: string) =>
  pathname.startsWith(HARNESS_PREFIX);

const CRM_TRIGGERS_PREFIX = '/admin/crm-triggers';
const isCrmTriggersActive = (pathname: string) =>
  pathname.startsWith(CRM_TRIGGERS_PREFIX);

/**
 * Pages that exist only off production (the API answers 404 there, and the
 * route redirects): left out of the menu on a production runtime.
 */
const PREVIEW_ONLY_URLS = new Set([CRM_TRIGGERS_PREFIX]);

const NAV_ITEMS: AdminNavItem[] = [
  {
    title: 'Music directory',
    url: '/admin/music',
    icon: Music,
    isActive: (pathname) => pathname.startsWith('/admin/music'),
  },
  {
    title: 'Impersonate',
    url: IMPERSONATE_PREFIX,
    icon: Users,
    isActive: isImpersonateActive,
  },
  {
    title: 'Imports',
    url: IMPORTS_PREFIX,
    icon: DatabaseZap,
    isActive: isImportsActive,
  },
  {
    title: 'Conversations',
    url: CONVERSATIONS_PREFIX,
    icon: MessageSquare,
    isActive: isConversationsActive,
  },
  {
    title: 'Advertising',
    url: ADVERTISING_PREFIX,
    icon: Megaphone,
    isActive: isAdvertisingActive,
  },
  // Platform admins only: billing state across every clinic.
  {
    title: 'Stuck in setup',
    url: STUCK_SETUP_PREFIX,
    icon: MegaphoneOff,
    isActive: isStuckSetupActive,
  },
  {
    title: 'Meta connections',
    url: META_CONNECTIONS_PREFIX,
    icon: PlugZap,
    isActive: isMetaConnectionsActive,
  },
  {
    title: 'Audit Log',
    url: AUDIT_LOG_PREFIX,
    icon: FileText,
    isActive: isAuditLogActive,
  },
  {
    title: 'Video',
    url: VIDEO_PREFIX,
    icon: Video,
    isActive: isVideoActive,
  },
  {
    title: 'Stock footage',
    url: STOCK_FOOTAGE_PREFIX,
    icon: Film,
    isActive: isStockFootageActive,
  },
  // Everywhere: live mode watches production traffic on the deployed API.
  // The replay tools inside it still only work against a local stack.
  {
    title: 'Harness',
    url: HARNESS_PREFIX,
    icon: FlaskConical,
    isActive: isHarnessActive,
  },
  // Platform admins only: it quotes customer messages across organizations.
  {
    title: 'Chatbot quality',
    url: CLAIRE_REGRESSIONS_PREFIX,
    icon: ChartLine,
    isActive: isClaireRegressionsActive,
  },
  // Platform admins only: same reason, confirmed replies from every clinic.
  {
    title: 'Chatbot failure groups',
    url: CLAIRE_FAILURE_GROUPS_PREFIX,
    icon: Layers,
    isActive: isClaireFailureGroupsActive,
  },
  // Preview only: fires the team CRM's automations by hand (ENG-2172).
  {
    title: 'CRM triggers',
    url: CRM_TRIGGERS_PREFIX,
    icon: Zap,
    isActive: isCrmTriggersActive,
  },
];

export function AdminSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { state, isMobile, setOpenMobile } = useSidebar();
  const { data: session } = useSession();
  const scoped = session?.user?.role === 'scoped-admin';
  const production = isProductionRuntime();
  const navItems = (
    scoped
      ? NAV_ITEMS.filter((item) =>
          [
            IMPERSONATE_PREFIX,
            IMPORTS_PREFIX,
            CONVERSATIONS_PREFIX,
            ADVERTISING_PREFIX,
          ].includes(item.url)
        )
      : NAV_ITEMS
  ).filter((item) => !(production && PREVIEW_ONLY_URLS.has(item.url)));
  const collapsed = state === 'collapsed';

  const handleNavigate = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="icon" variant="sidebar" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Admin"
              size="lg"
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <div className="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-md">
                <Shield className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">Admin</span>
                <span className="text-muted-foreground truncate text-xs">
                  Platform tools
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const active = item.isActive(pathname);
                const Icon = item.icon;

                if (item.items?.length) {
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        tooltip={item.title}
                        asChild
                        isActive={active}
                      >
                        <Link to={item.url} onClick={handleNavigate}>
                          <Icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                      {!collapsed && active ? (
                        <SidebarMenuSub>
                          {item.items.map((sub) => {
                            const SubIcon = sub.icon;
                            const subActive = sub.isActive(pathname);
                            return (
                              <SidebarMenuSubItem key={sub.title}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={subActive}
                                >
                                  <Link to={sub.url} onClick={handleNavigate}>
                                    <SubIcon className="size-3.5" />
                                    <span>{sub.title}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      ) : null}
                    </SidebarMenuItem>
                  );
                }

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      tooltip={item.title}
                      asChild
                      isActive={active}
                    >
                      <Link to={item.url} onClick={handleNavigate}>
                        <Icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton tooltip="Back to dashboard" asChild>
              <Link to={ROUTES.dashboard} onClick={handleNavigate}>
                <ArrowLeft />
                <span>Back to dashboard</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
